import { useEffect, useRef, useState } from 'react';
import { Hand, Search } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { useAuthStore } from '@/store/auth.store';
import { guiasParaMostrar } from '@/lib/guia-motor';
import { useGuia } from './guia-contexto';

/**
 * "¿CÓMO HAGO…?" — las guías de "Muéstrame" sin pasar por el Asistente.
 *
 * La misma guía que ofrece el Asistente, a mano: se busca con palabras de todos
 * los días ("horario", "técnico", "mensaje") y se toca. Sirve aunque la IA no
 * esté disponible (sin clave, tope de gasto), que es justo cuando alguien más
 * necesita que el sistema le explique solo.
 */
export function MenuGuias({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [busqueda, setBusqueda] = useState('');
  const rol = useAuthStore((s) => s.user?.role);
  const guia = useGuia();
  const campo = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setBusqueda('');
    const t = window.setTimeout(() => campo.current?.focus(), 50);
    return () => window.clearTimeout(t);
  }, [open]);

  const guias = guiasParaMostrar(busqueda, rol);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="¿Cómo hago…?"
      description="Elige lo que quieres hacer y te voy mostrando dónde tocar, paso a paso."
    >
      <label className="relative block">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
          aria-hidden
        />
        <input
          ref={campo}
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="Escribe con tus palabras: horario, técnico, mensaje…"
          aria-label="Buscar qué quieres hacer"
          className="w-full rounded-md border border-surface-border bg-transparent py-2 pl-9 pr-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-slate-700"
        />
      </label>

      <ul className="mt-3 max-h-80 space-y-1 overflow-y-auto">
        {guias.length === 0 && (
          <li className="px-2 py-3 text-sm text-slate-500">
            No tengo una guía para eso. Pregúntale al asistente o cuéntalo en «Qué quieres que
            cambiemos».
          </li>
        )}
        {guias.map((r) => (
          <li key={r.id}>
            <button
              type="button"
              onClick={() => {
                onClose();
                guia.iniciar(r.id);
              }}
              className="flex w-full items-center gap-3 rounded-md px-2.5 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 focus-visible:bg-slate-100 focus-visible:outline-none dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <Hand className="h-4 w-4 shrink-0 text-brand-600" aria-hidden />
              <span className="min-w-0 flex-1">{r.titulo}</span>
              <span className="shrink-0 text-xs text-slate-400">
                {r.pasos.length} {r.pasos.length === 1 ? 'paso' : 'pasos'}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </Modal>
  );
}
